import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TaxonomyCategory } from '@/entities/taxonomy-category';

interface CategoryActionMenuProps {
  category: TaxonomyCategory;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Row actions for a category. Raises intent only — the page owns the modals,
 * because a dialog rendered here would be unmounted by its own success when
 * the row disappears, stranding `body { pointer-events: none }` and freezing
 * the page (the same trap the Fields and Lists menus hit).
 */
export function CategoryActionMenu({ category, onEdit, onDelete }: CategoryActionMenuProps) {
  const { t } = useTranslation('knowledge');
  return (
    // Portals bubble through the REACT tree, so without this the menu's own
    // clicks reach the row.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('taxonomy.categoryActionMenu.actionsFor', { name: category.name })}
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil className="size-4" />
            {t('taxonomy.categoryActionMenu.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
            <Trash2 className="size-4" />
            {t('taxonomy.categoryActionMenu.deleteCategory')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

CategoryActionMenu.displayName = 'CategoryActionMenu';
